import Header from '../.shared/header/header';
import Footer from '../.shared/footer/footer';

import styles from './Terms.module.css';


const TermsContent = () => (    
    <div className={styles.container}>
        <h1>Terms and Conditions</h1>
        <p>Last updated: 04/09/2026</p>
        <p>Please read these Terms and Conditions ("Terms", "Terms and Conditions") carefully 
            before using tb2.health ("the Website") operated by Pillars For Health, L.L.C. 
            ("us", "we", or "our").</p>
        <p>Your access to and use of the Website is conditioned upon your acceptance of and 
            compliance with these Terms. These Terms apply to all visitors, users, and others 
            who wish to access or use the Website.</p>
        <p>By accessing or using the Website, you agree to be bound by these Terms. If you 
            disagree with any part of the terms, then you do not have permission to access the Website.</p>

        <h2>1. Use of Telehealth Services</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>The Website provides a telehealth-enabled platform for non-clinical services, 
                    including technology infrastructure, patient intake, care coordination, customer support, 
                    and health-related information ("Services"). Clinical services and prescribing decisions 
                    are made solely by licensed, independent healthcare providers.</li>
                <li>The Services provided through the Website are not a substitute 
                    for in-person medical care or emergency services. If you are experiencing 
                    a medical emergency, please call your local emergency number immediately.</li>
            </ol>
        </div>

        <h2>2. Medical Advice</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>The information provided on the Website is for general informational purposes 
                    only and is not intended as medical advice.</li>
                <li>Use of the platform does not itself establish a doctor-patient relationship with Pillars For Health, L.L.C.; 
                    however, a doctor-patient relationship may be established between you and a licensed healthcare provider if 
                    you engage in a clinical consultation through the platform.</li>
            </ol>
        </div>

        <h2>3. Privacy</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>Your privacy is important to us. Please refer to 
                    our <a href='./Privacy'>Privacy Policy</a> to understand how we collect, use, and 
                    protect your personal information.</li>
            </ol>
        </div>

        <h2>4. User Accounts</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>In order to access certain features of the Services, you may be required to create an account. 
                    You are responsible for maintaining the confidentiality of your account and password and for 
                    restricting access to your computer or device. You agree to accept responsibility for all 
                    activities that occur under your account or password.</li>
                <li>You must be at least 18 years old to create an account on the Website.</li>
            </ol>
        </div>

        <h2>5. User Responsibilities</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>You agree to use the Services in a responsible and lawful manner and to comply with all 
                    applicable laws and regulations. You agree not to use the Services for any unlawful, harmful, 
                    or malicious purposes. </li>
                <li>You agree to provide accurate, current, and complete registration information.</li>
                <li>You commit to maintaining the accuracy of your provided information and promptly updating it 
                    when necessary.</li>
                <li>By using the Site, you confirm that you possess the legal capacity to do so and agree to abide 
                    by these Terms and Conditions.</li>
                <li>You certify that you are at least 18 years old.</li>
                <li>You represent that you meet all age and legal-eligibility requirements applicable to the Site 
                    and Services in your jurisdiction.</li>
                <li>You agree not to access the Site using automated or non-human methods, including bots or scripts.</li>
                <li>You will not employ the Site for any unlawful or unauthorized purposes.</li>
                <li>Your use of the Site will comply with all applicable laws and regulations and will not breach any 
                    legal obligations.</li>
            </ol>
        </div>

        <h2>6. Service Providers and Patient/Billing Portals</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>We use third-party service providers to support platform operations and related services. 
                    Some services may be operated directly by us, while others may be operated by third-party 
                    providers acting on our behalf.</li>
                <li><b>Patient Portal (patient.tb2.health):</b> Our patient portal may be operated either by us 
                    directly or by a third-party service provider under a white-label or hosted arrangement. 
                    If the portal is operated by a third-party provider, you acknowledge that:
                    <ul>
                        <li>The portal may have different availability, performance, or functionality than our 
                            main website</li>
                        <li>We have limited control over the portal's technical infrastructure and operations</li>
                        <li>Portal downtime or technical issues are managed by the third-party provider</li>
                        <li>You are subject to the third-party provider's terms of service and privacy policy 
                            in addition to ours</li>
                    </ul>
                </li>
                <li><b>Billing Portal (billing.tb2.health):</b> Our billing portal may be operated by us or by a 
                    third-party payment processing service under a white-label or hosted arrangement. 
                    If third-party operated, you acknowledge that:
                    <ul>
                        <li>Payment processing and billing functions are handled by the external provider</li>
                        <li>We remain the merchant of record and are responsible for customer support, refunds, 
                            and chargeback handling, although resolution may involve coordination with third-party 
                            service providers.</li>
                        <li>Your payment information is subject to the third-party provider's security measures 
                            and data handling practices</li>
                    </ul>
                </li>
                <li><b>Limitation of Liability for Third-Party Services:</b> To the maximum extent permitted by law, 
                    when a portal or service is operated by a third-party provider, we disclaim liability for 
                    issues arising at the third-party infrastructure level, including service interruptions, data 
                    loss, security incidents at the third-party level, or transaction processing failures. 
                    Nothing in this section limits our responsibilities as merchant of record for transactions 
                    processed through our platform.</li>
                <li><b>Service Availability:</b> We make reasonable efforts to ensure third-party services remain 
                    available and functional, but we cannot guarantee uninterrupted access. Scheduled maintenance, 
                    technical issues, or service provider changes may temporarily affect portal availability.</li>
                <li><b>Data Security Responsibility:</b> While we require our third-party providers to maintain 
                    appropriate security measures and HIPAA compliance, you acknowledge that data security involves 
                    shared responsibility between us, our third-party providers, and your own security practices 
                    (such as maintaining secure passwords and using trusted devices).</li>
            </ol>
        </div>

        <h2>7. GLP-1 Weight Loss Program</h2>
        <div className={styles.paragraph}>
            In addition to the general terms and conditions outlined above, if you choose to enroll in 
            our GLP-1 weight loss program, the following additional terms and conditions apply:            
            <ol>
                <li>
                    <h4>Important Notice</h4>
                    <ul>
                        <li>Participation in the program includes access to non-clinical services. Prescription decisions are made
                            independently by licensed healthcare providers, and medication is dispensed and shipped by licensed
                            pharmacies.</li>
                    </ul>
                </li>
                <li>
                    <h4>Initial Eligibility Assessment and Diagnostic Tests</h4>
                    <ul>
                        <li>Before participating in the GLP-1 weight loss program, a qualified clinician 
                            will assess your eligibility based on your responses to intake questions. You must 
                            be 18 years of age or older and have a BMI of 27 or higher. In some cases, the 
                            clinician may require additional diagnostic tests or laboratory monitoring, including 
                            but not limited to lipase levels, kidney function, and thyroid function, to make an 
                            accurate determination.</li>
                        <li>Eligibility for participation is solely at the discretion of the clinician, 
                            who may consider various factors, including your medical history, current 
                            health status, and test results.</li>
                        <li><b>Medical Contraindications:</b> Patients with the following conditions are NOT 
                            eligible for GLP-1 therapy:
                            <ul>
                                <li>Personal or family history of medullary thyroid carcinoma (MTC)</li>
                                <li>Multiple Endocrine Neoplasia syndrome type 2 (MEN2)</li>
                                <li>History of severe gastroparesis or diabetic gastroparesis</li>
                                <li>Active gallbladder disease</li>
                                <li>History of pancreatitis</li>
                                <li>Pregnant or breastfeeding women</li>
                                <li>History of severe gastrointestinal disease</li>
                                <li>Type 1 diabetes</li>
                                <li>Use of insulin or sulfonylureas (requires medical clearance)</li>
                            </ul>
                        </li>
                        <li><b>Medication Information:</b> The GLP-1 medications provided through this program 
                            are compounded alternatives prepared by licensed compounding pharmacies. These 
                            medications are not FDA-approved brand name products. Healthcare providers are licensed 
                            to practice in applicable jurisdictions and provide services in accordance with state 
                            and federal telemedicine laws.</li>
                    </ul>
                </li>
                <li>
                    <h4>Monthly Clinical Progress Review and Dosage Adjustment</h4>
                    <ul>
                        <li>Throughout your participation in the program, the clinician will conduct a 
                            comprehensive review of your progress on a monthly basis. GLP-1 medications 
                            follow FDA-approved escalation schedules with dosage increases every 4 weeks 
                            as tolerated, under medical supervision. Based on this assessment, the clinician 
                            may recommend adjustments to your GLP-1 dosage or provide additional guidance as needed.</li>
                        <li>These monthly evaluations are crucial for tailoring your treatment to your 
                            specific needs and ensuring its effectiveness. Regular clinical assessments 
                            include monitoring for side effects, weight progress, and contraindication 
                            development.</li>
                        <li>Patients must report any adverse effects immediately to their healthcare provider. 
                            Final medical decisions regarding treatment rest with licensed providers, not the platform.</li>
                    </ul>
                </li>
                <li>
                    <h4>Money Back Guarantee</h4>
                    <ul>
                        <li>You are entitled to a full refund if you are not approved for a prescription following your medical
                            assessment by a licensed clinician. This assessment typically occurs within 1–2 business days of
                            enrollment.</li>
                        <li>If a prescription is issued, refunds are not available once the medication has been dispensed or
                            shipped by the pharmacy.</li>
                        <li>If you cancel after being approved but prior to prescription fulfillment or shipment, you may be eligible
                            for a refund or account credit, subject to review.</li>
                        <li>Refunds for ineligible applicants will be processed within a reasonable timeframe, typically within 7–14
                            business days.</li>
                    </ul>
                </li>
                <li>
                    <h4>Progress Updates</h4>
                    <ul>
                        <li>As a participant in the program, you may be required to provide monthly or weekly updates 
                            on your progress. This may include reporting your medication adherence, any side 
                            effects experienced, and changes in your overall health.</li>
                        <li>Timely and accurate progress updates are essential for ensuring your safety and 
                            optimizing your treatment plan.</li>
                    </ul>
                </li>
                <li>
                    <h4>Clinician's Right to Terminate</h4>
                    <ul>
                        <li>The clinician reserves the right to terminate your participation in the GLP-1 
                            weight loss program at their discretion. This may occur if your medical condition 
                            changes, if you do not adhere to program guidelines, or if there are any concerns 
                            regarding your safety or the effectiveness of the treatment.</li>
                        <li>In the event of termination after prescription fulfillment or shipment has occurred, 
                            fees already paid may not be refundable.</li>
                    </ul>
                </li>
                <li>
                    <h4>Patient's Right to Cancel</h4>
                    <ul>
                        <li>You have the right to cancel your participation in the GLP-1 weight loss program at 
                            any time, for any reason, without incurring any additional fees or penalties.</li>
                        <li>To cancel, please notify our support team at support@tb2.health or visit
                            our billing portal at https://tb2.health/billing.</li>                    
                    </ul>
                </li>
                <li>
                    <h4>Up-Front Payment</h4>
                    <ul>
                        <li>You agree to pay any applicable fees for the GLP-1 weight loss program as outlined 
                            during the enrollment process.</li>
                        <li>Fees may vary based on the services provided and the duration of your participation.</li>
                        <li>Payment is due before you gain access to the program.</li>
                    </ul>
                </li>
                <li>
                    <h4>Program Renewal and Charges</h4>
                    <ul>
                        <li>The GLP-1 weight loss program may renew automatically at the end of the selected
                            term, unless you choose to cancel it.</li>
                        <li>If you do not wish to renew your program, you must cancel it before the renewal date
                            to avoid additional charges.</li>
                    </ul>
                </li>
            </ol>
        </div>

        <h2>8. Data Security and HIPAA Compliance</h2>
        <div className={styles.paragraph}>
            <ol>
                <li><b>Business Associate Agreements:</b> Where required by applicable law, third-party service 
                    providers handling protected health information (PHI) on our behalf will be subject to 
                    Business Associate Agreements (BAAs) or equivalent contractual requirements.</li>
                <li><b>Data Encryption and Security:</b> Data transmission between our systems and third-party 
                    portals uses industry-standard encryption protocols (TLS 1.2 or higher). Third-party providers 
                    are required to maintain appropriate technical, physical, and administrative safeguards for 
                    PHI protection.</li>
                <li><b>Portal Operating Model:</b> If a patient or billing portal is operated directly by us, data 
                    handling is governed by our own security and privacy controls. If a portal is operated by a 
                    third-party provider, data handling is also subject to that provider's controls and contractual 
                    obligations to us.</li>
                <li><b>Cross-Border Data Processing:</b> If any third-party providers process your data outside 
                    the United States, we ensure that appropriate data protection agreements are in place and 
                    that such transfers comply with applicable privacy laws and healthcare regulations.</li>
                <li><b>Audit and Compliance Monitoring:</b> We regularly review our third-party providers' 
                    security practices and compliance status. However, you acknowledge that we rely on their 
                    representations and certifications regarding their security measures and compliance status.</li>
                <li><b>Data Breach Notification:</b> In the event of a data security incident involving a 
                    third-party provider, we will coordinate breach response efforts and provide required 
                    notifications in accordance with HIPAA breach notification requirements and applicable 
                    state laws.</li>
            </ol>
        </div>

        <h2>9. Medical Regulations and Disclaimers</h2>
        <div className={styles.paragraph}>
            <ol>
                <li><b>Compounded Medications:</b> The GLP-1 medications provided through this program are 
                    compounded alternatives prepared by licensed compounding pharmacies under FDA regulations 
                    for compounded medications. These are not FDA-approved brand name products. Compounded 
                    medications are regulated differently than FDA-approved drugs and may have different 
                    safety and efficacy profiles.</li>
                <li><b>Telemedicine Licensing:</b> Our healthcare providers are licensed to practice medicine 
                    in all 50 states and provide telemedicine services in compliance with state and federal 
                    regulations.</li>
                <li><b>Clinical Decision Authority:</b> All medical decisions, including prescription approval, 
                    dosage adjustments, and treatment modifications, are made solely by licensed healthcare 
                    providers. The platform facilitates but does not make medical decisions.</li>
                <li><b>Emergency Situations:</b> This service is not appropriate for medical emergencies. 
                    In case of emergency, call 911 or go to your nearest emergency room immediately.</li>
            </ol>
        </div>

        <h2>10. Intellectual Property</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>The content on the Website, including but not limited to text, graphics, logos, and images, 
                    is protected by copyright and other intellectual property laws.</li>
                <li>You may not use, reproduce, distribute, or create derivative works based on the content without 
                    our express written consent.</li>
            </ol>
        </div>

        <h2>11. Changes to Terms and Conditions</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>We reserve the right to modify or replace these Terms at any time. It is your responsibility to 
                    review these Terms periodically for changes.</li>
            </ol>
        </div>

        <h2>12. Binding Arbitration</h2>
        <div className={styles.paragraph}>
            <ol>
                <li>To expedite the resolution and manage the expenses of any dispute, disagreement, or claim 
                    arising from these Terms and Conditions (referred to individually as a "Dispute" and 
                    collectively as "Disputes"), any Dispute initiated by you or us (individually a "Party" 
                    and collectively the "Parties") shall be conclusively and exclusively settled through 
                    binding arbitration.</li>
                <li>YOU ACKNOWLEDGE THAT WITHOUT THIS PROVISION, YOU WOULD HAVE THE RIGHT TO INITIATE A LAWSUIT 
                    IN COURT AND HAVE A JURY TRIAL. The arbitration process will be initiated and conducted 
                    according to the Commercial Arbitration Rules of the American Arbitration Association ("AAA"), 
                    and where applicable, the AAA's Supplementary Procedures for Consumer-Related Disputes 
                    ("AAA Consumer Rules"), accessible at the AAA's website: <a href='https://www.adr.org' target='_blank' rel='noreferrer'>www.adr.org</a>.</li>
                <li>Your arbitration expenses and your portion of arbitrator compensation will be subject to the 
                    guidelines established by the AAA Consumer Rules and may also be limited by those rules. 
                    Should the arbitrator consider these costs excessive, we will bear the responsibility for 
                    covering all arbitration fees and costs.</li>
                <li>The arbitration proceedings may occur in person, through document submissions, over the phone, 
                    or online. The arbitrator's decision will be documented in writing, with the exception that a 
                    statement of reasons need not be provided unless explicitly requested by either Party. The 
                    arbitrator must adhere to relevant legal principles, and any awarded decision may be contested 
                    if the arbitrator fails to do so.</li>
                <li>Unless mandated otherwise by applicable AAA regulations or law, the arbitration proceedings 
                    will take place in Summit County, Ohio. Aside from the provisions outlined herein, 
                    the Parties maintain the right to seek resolution through litigation in court, specifically for 
                    compelling arbitration, postponing proceedings while arbitration is pending, or to validate, 
                    amend, annul, or enter a judgment based on the arbitrator's decision.</li>
                <li>Should a Dispute be processed in court instead of arbitration for any reason, such Dispute will 
                    be initiated or pursued within the state and federal courts located in Summit County, Ohio. 
                    By agreeing to these terms, the Parties hereby waive any defenses related to lack of 
                    personal jurisdiction or forum non conveniens, thereby consenting to the venue and jurisdiction 
                    of such state and federal courts.</li>
                <li>Application of the United Nations Convention on Contracts for the International Sale of Goods 
                    and the Uniform Computer Information Transaction Act (UCITA) are expressly excluded from the 
                    scope of these Terms and Conditions. Under no circumstances shall any Dispute initiated by 
                    either Party regarding the Site or Services be filed more than 1 year after the origination of 
                    the cause of action.</li>
                <li>Should this provision be deemed unlawful or unenforceable, neither Party shall choose to 
                    arbitrate any Dispute falling under that portion of this provision, and such Dispute shall be 
                    resolved by a competent court listed within the jurisdiction above. The Parties mutually agree 
                    to subject themselves to the personal jurisdiction of that court.</li>
            </ol>
        </div>

        <h2>13. Disclaimer</h2>
        <div className={styles.paragraph}>
            THE SITE IS PROVIDED "AS-IS" AND "AS-AVAILABLE." YOUR USE OF THE SITE AND OUR SERVICES IS ENTIRELY AT 
            YOUR SOLE RISK. TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, WHETHER 
            EXPRESS OR IMPLIED, RELATED TO THE SITE AND YOUR USE OF IT, INCLUDING BUT NOT LIMITED TO IMPLIED 
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE MAKE NO 
            WARRANTIES OR REPRESENTATIONS REGARDING THE ACCURACY OR COMPLETENESS OF THE SITE'S CONTENT OR THE 
            CONTENT OF ANY LINKED WEBSITES. WE ASSUME NO LIABILITY OR RESPONSIBILITY FOR: 
            <ol className={styles.numberedList}>
                <li>ANY ERRORS, MISTAKES, OR INACCURACIES OF CONTENT AND MATERIALS</li>
                <li>ANY PERSONAL INJURY OR PROPERTY DAMAGE ARISING FROM YOUR ACCESS TO AND USE OF THE SITE</li>
                <li>ANY UNAUTHORIZED ACCESS TO OR USE OF OUR SECURE SERVERS AND/OR ANY PERSONAL OR FINANCIAL 
                    INFORMATION STORED THEREIN</li>
                <li>ANY INTERRUPTION OR CESSATION OF TRANSMISSION TO OR FROM THE SITE</li>
                <li>ANY BUGS, VIRUSES, TROJAN HORSES, OR SIMILAR MALICIOUS CODE TRANSMITTED THROUGH THE SITE BY 
                    THIRD PARTIES</li>
                <li>ANY ERRORS OR OMISSIONS IN THE SITE'S CONTENT AND MATERIALS, OR FOR ANY LOSS OR DAMAGE INCURRED 
                    AS A RESULT OF USING ANY CONTENT POSTED, TRANSMITTED, OR OTHERWISE MADE AVAILABLE THROUGH THE 
                    SITE</li>
            </ol>
        </div>
        <div className={styles.paragraph}>
            WE DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY PRODUCT OR SERVICE ADVERTISED 
            OR OFFERED BY THIRD PARTIES THROUGH THE SITE OR ANY LINKED WEBSITE. FURTHERMORE, WE WILL NOT BE A PARTY 
            TO OR IN ANY WAY RESPONSIBLE FOR MONITORING TRANSACTIONS BETWEEN YOU AND THIRD-PARTY PROVIDERS OF 
            PRODUCTS OR SERVICES.
        </div>
        <div className={styles.paragraph}>
            <b>PORTAL DISCLAIMERS:</b> OUR PATIENT PORTAL AND BILLING PORTAL MAY BE OPERATED BY US OR BY THIRD-PARTY 
            SERVICE PROVIDERS. WHEN THIRD-PARTY PROVIDERS OPERATE THESE PORTALS, WE MAKE REASONABLE EFFORTS TO 
            ENSURE THESE SERVICES MEET OUR STANDARDS, BUT WE DISCLAIM LIABILITY FOR THIRD-PARTY PORTAL AVAILABILITY, 
            PERFORMANCE, DATA SECURITY INCIDENTS AT THE THIRD-PARTY LEVEL, OR SERVICE INTERRUPTIONS. YOUR USE OF 
            THIRD-PARTY-OPERATED PORTALS IS SUBJECT TO BOTH OUR TERMS AND, WHERE APPLICABLE, THE THIRD-PARTY 
            PROVIDERS' TERMS OF SERVICE. NOTHING IN THIS DISCLAIMER LIMITS OUR RESPONSIBILITIES AS MERCHANT OF 
            RECORD FOR TRANSACTIONS PROCESSED THROUGH OUR PLATFORM.
        </div>
        <div className={styles.paragraph}>
            SIMILARLY, AS WITH THE PURCHASE OF ANY PRODUCT OR SERVICE THROUGH ANY MEDIUM OR ENVIRONMENT, YOU SHOULD 
            EXERCISE CAUTION, USE YOUR BEST JUDGMENT, AND MAKE INFORMED DECISIONS.
        </div>

        <h2>14. Limitations of Liability</h2>
        <div className={styles.paragraph}>
            WE, ALONG WITH OUR DIRECTORS, EMPLOYEES, AND AGENTS, SHALL NOT BE LIABLE TO YOU OR ANY THIRD PARTY FOR 
            ANY DIRECT, INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES. THESE DAMAGES 
            INCLUDE BUT ARE NOT LIMITED TO LOST PROFITS, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES THAT MAY 
            ARISE FROM YOUR USE OF THE SITE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </div>
        <div className={styles.paragraph}>
            WITHOUT REGARD TO ANY CONTRARY PROVISIONS HEREIN, OUR LIABILITY TO YOU FOR ANY REASON WHATSOEVER AND 
            REGARDLESS OF THE FORM OF THE LEGAL ACTION SHALL ALWAYS BE CAPPED AT THE LESSER OF (A) THE AMOUNT YOU 
            PAID, IF ANY, TO US DURING THE <b>12 MONTH PERIOD BEFORE THE OCCURRENCE OF THE INCIDENT GIVING RISE TO THE CLAIM</b> 
            OR (B) <b>$1,000</b>. CERTAIN STATE LAWS MAY NOT ALLOW THE LIMITATION OF IMPLIED WARRANTIES OR THE EXCLUSION OR LIMITATION 
            OF CERTAIN DAMAGES. 
        </div>
        <div className={styles.paragraph}>
            IF THESE LAWS APPLY TO YOU, SOME OR ALL OF THE ABOVE DISCLAIMERS OR LIMITATIONS MAY NOT BE APPLICABLE, AND YOU 
            MAY BE ENTITLED TO ADDITIONAL RIGHTS. 
        </div>

        <h2>15. Indemnification</h2>
        <div className={styles.paragraph}>
            You hereby commit to defend, indemnify, and shield us from any loss, damage, liability, claim, or demand. This 
            extends to our subsidiaries, affiliates, as well as all officers, agents, partners, and employees associated with 
            us. This coverage includes reasonable attorneys' fees and expenses incurred as a result of:
            <ol>
                <li>Your Contributions</li>
                <li>Your use of the Site</li>
                <li>Any violation of these Terms and Conditions</li>
                <li>Any breach of the representations and warranties you've made in these Terms and Conditions</li>
                <li>Any infringement upon the rights of a third party, including intellectual property rights</li>
                <li>Any overt harmful actions directed toward another user of the Site with whom you interacted via the Site</li>
            </ol>
        </div>
        <div className={styles.paragraph}>        
            However, we retain the right to, at your cost, assume exclusive control and defense of any matter requiring your 
            indemnification. You agree to cooperate, at your expense, in our defense against such claims. We will make 
            reasonable attempts to notify you of any such claims, actions, or proceedings subject to this indemnification, 
            once we are made aware of them.
        </div>

        <h2>16. Electronic Communications, Transactions, and Signatures</h2>
        <div className={styles.paragraph}>
            Your interaction with the Site, sending us emails, and filling out online forms constitute forms of electronic 
            communication. By continuing to use these methods, you grant consent to receive electronic communications. 
            Furthermore, you acknowledge that any agreements, notifications, disclosures, and other communications we furnish 
            to you electronically, whether via email or on the Site, fulfill any legal necessity for such communications to 
            be in writing.
        </div>
        <div className={styles.paragraph}>
            Additionally, you hereby provide your consent for the utilization of electronic signatures, contracts, orders, 
            and other records, as well as for the electronic distribution of notices, policies, and records pertaining to 
            transactions conducted by us or through the Site.
        </div>
        <div className={styles.paragraph}>
            Through this agreement, you waive any rights or prerequisites under any laws, statutes, regulations, ordinances, 
            or other legislative measures in any jurisdiction that require an original signature or the preservation or 
            provision of non-electronic records. This also includes matters relating to payments or the issuance of credits 
            by any means other than electronic methods.
        </div>

        <h2>17. Miscellaneous</h2>
        <div className={styles.paragraph}>
            These Terms and Conditions, alongside any policies or operational guidelines we post on the Site, encompass the 
            entire agreement and comprehension between you and us. Our failure to exercise or enforce any right or provision 
            stipulated in these Terms and Conditions shall not be deemed a waiver of said right or provision.
        </div>
        <div className={styles.paragraph}>
            These Terms and Conditions are upheld to the fullest extent allowable by law. We retain the prerogative to assign 
            any or all of our rights and responsibilities to others whenever necessary. We shall not be held accountable or 
            liable for any loss, damage, delay, or inability to act, resulting from any circumstances beyond our reasonable control.
        </div>
        <div className={styles.paragraph}>
            Should any section or component of these Terms and Conditions be determined to be unlawful, void, or unenforceable, 
            such portion shall be considered separable from these Terms and Conditions and shall not impact the validity and 
            enforceability of the remaining provisions.
        </div>
        <div className={styles.paragraph}>        
            This agreement does not establish a joint venture, partnership, employment relationship, or agency affiliation between 
            you and us, as a result of these Terms and Conditions or your use of the Site. You agree that these Terms and Conditions 
            will not be construed against us on the grounds of our role in drafting them.
        </div>
        <div className={styles.paragraph}>        
            By using the Site, you relinquish any and all defenses you might otherwise have based on the electronic format of these 
            Terms and Conditions and the absence of physical signatures by the parties involved to execute these Terms and Conditions.
        </div>

        <h2>18. Governing Law</h2>
        <div className={styles.paragraph}>
            These Terms shall be governed by and construed in accordance with the laws of Delaware, without regard to its conflict 
            of law provisions.
        </div>

        If you have any questions or concerns about these Terms and Conditions, please contact us at privacy@tb2.health
    </div>
);

function Terms() {
    return (
        <div>
            <Header />
            <TermsContent />
            <Footer />
        </div>
    );
}

export default Terms;